import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  courseAppearanceService,
  type CreatePageSectionInput,
  type PageSection,
} from '@/services/course-appearance.service';
import { appearanceKeys } from './queryKeys';

/**
 * Create a page section. Optimistically appends a placeholder; the resolved
 * value of mutateAsync is the real server section (used to open its modal).
 */
export function useCreatePageSection(productId: string) {
  const qc = useQueryClient();
  const key = appearanceKeys.sections(productId);

  return useMutation({
    mutationFn: (input: CreatePageSectionInput) =>
      courseAppearanceService.createPageSection(productId, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PageSection[]>(key);
      const now = new Date().toISOString();
      const temp: PageSection = {
        id: `temp-${Date.now()}`,
        productId,
        type: input.type,
        order: input.order ?? prev?.length ?? 0,
        isVisible: input.isVisible ?? true,
        config: input.config ?? {},
        createdAt: now,
        updatedAt: now,
      };
      qc.setQueryData<PageSection[]>(key, [...(prev ?? []), temp]);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(i18n.t('adminAppearance:toasts.createSectionError'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
